import './App.less';
import "./assets/layout.sass";

import React from 'react';
import { HashRouter as Router, Redirect, Route, Switch } from 'react-router-dom'
import DashboardPage from './views/containers/DashboardPage'
import DefaultLayout from './views/containers/DefaultLayout';
import NotFoundPage from './views/containers/NotFoundPage';
import SalesPage from './views/containers/reports/SalesPage';
import HouseRatePage from './views/containers/reports/HouseRatePage';
import RoomTypePage from './views/containers/reports/RoomTypePage';
import GuestPage from './views/containers/reports/GuestPage';
import RepeatGuestPage from './views/containers/reports/RepeatGuestPage';
import NotificationSettingPage from './views/containers/settings/NotificationSettingPage';
import HotelPage from './views/containers/pricing/HotelPage';
import RoomCategoryPage from './views/containers/pricing/RoomCategoryPage';
import PricingRoomTypePage from './views/containers/pricing/RoomTypePage';
import { Skeleton } from 'antd';
import LoginPage from './views/containers/LoginPage';
import InvitePage from './views/containers/InvitePage';
import InviteSignedInNotice from './views/containers/InviteSignedInNotice';
import UserPage from './views/containers/settings/UserPage';
import DepartmentPage from './views/containers/settings/DepartmentPage';
import ChannelCommissionPage from './views/containers/settings/ChannelCommissionPage';
import HolidayPage from './views/containers/settings/HolidayPage';
import ChannelPage from './views/containers/reports/ChannelPage';
import PromotionPage from './views/containers/reports/PromotionPage';
import RevParComparisonPage from './views/containers/reports/RevParComparisonPage';
import RateCalendarPage from './views/containers/reports/RateCalendarPage';
import OpsDashboard from './views/containers/OpsDashboard';
import InventoryPage from './views/containers/reports/InventoryPage';
import RebalancePage from './views/containers/reports/RebalancePage';
import ExposurePage from './views/containers/reports/ExposurePage';
import { PricingAPI } from './controller/PricingAPI';
import TodoPage from './views/containers/pricing/TodoPage';
import RoutinePage from './views/containers/pricing/RoutinePage';
import { landingPath, visibleItems } from './navigation';
import LoginLogPage from './views/containers/settings/LoginLogPage';
import SystemSettingsPage from './views/containers/settings/SystemSettingsPage';
import SurveyCakeSurveyPage from './views/containers/settings/SurveyCakeSurveyPage';

// navigation.js 只放選單資料，頁面元件對應在這裡 ——
// navigation.js 若 import 這些元件，它的單元測試會連帶載入 echarts 而跑不動
const PAGE_COMPONENTS = {
  todos: TodoPage,
  routines: RoutinePage,
  dashboard: DashboardPage,
  hotels: HotelPage,
  roomcategories: RoomCategoryPage,
  pricingroomtypes: PricingRoomTypePage,
  inventory: InventoryPage,
  rebalance: RebalancePage,
  exposure: ExposurePage,
  'ops-dashboard': OpsDashboard,
  sales: SalesPage,
  houserate: HouseRatePage,
  roomtypes: RoomTypePage,
  guests: GuestPage,
  'repeat-guests': RepeatGuestPage,
  channels: ChannelPage,
  promotions: PromotionPage,
  'revpar-comparison': RevParComparisonPage,
  'rate-calendar': RateCalendarPage,
  notification: NotificationSettingPage,
  users: UserPage,
  departments: DepartmentPage,
  'login-logs': LoginLogPage,
  'channel-commission': ChannelCommissionPage,
  holidays: HolidayPage,
  system: SystemSettingsPage,
  'surveycake-surveys': SurveyCakeSurveyPage,
};

function App() {
  const [logined, setLogined] = React.useState(false);
  // 初值就是 true，讓第一次 commit 就顯示 Skeleton、不掛載 Router——
  // 若初值是 false，第一次 commit 會先掛出 Router (連帶掛載 InvitePage
  // 並發出它的 API 呼叫)，緊接著這個 effect 才把 loading 設回 true 讓
  // Router 整層卸載重掛，等於同一次開頁打兩次 API
  const [loading, setLoading] = React.useState(true);

  PricingAPI.auth.init(setLogined);

  React.useEffect(() => {
    PricingAPI.auth.loginWithToken().finally(() => {
      setLoading(false);
    });
  }, [setLoading]);

  // 沒有權限的路徑不註冊 Route，一律落到 NotFoundPage ——
  // 只藏選單不夠，業務手動輸入網址會停在 API 403 的錯誤畫面，看起來像系統故障
  const permissions = PricingAPI.auth.getPermissions();
  const allowedItems = visibleItems(permissions);
  const homePath = landingPath(permissions);

  return (
    <div className="App">
      {loading ? <Skeleton /> :
        <Router>
          {logined ?
            // DefaultLayout 包在 Switch 外層，換頁時只換內容不重新掛載版面，
            // 側邊欄的收合狀態才不會每次點選單就被重置
            <DefaultLayout>
              <Switch>
                <Route path="/" exact>
                  {homePath ? <Redirect to={homePath} /> : <NotFoundPage />}
                </Route>
                {/* 已登入時也要接住邀請連結。直接落到 NotFoundPage 會讓人以為連結壞了，
                    但常見情境只是管理員自己點開檢查，或受邀者用了別人登入過的瀏覽器。 */}
                <Route path="/invite/:token" exact children={<InviteSignedInNotice />} />
                {allowedItems.map((item) => {
                  const Page = PAGE_COMPONENTS[item.key];
                  return Page ? (
                    <Route key={item.key} path={item.path} exact children={<Page />} />
                  ) : null;
                })}
                {/* 信件裡的深層連結。權限與 /todos 同一道 (沒有權限時不註冊，
                    落到 NotFoundPage)，不可以因為多一條路由而繞過權限 */}
                {allowedItems.some((item) => item.key === 'todos') ? (
                  <Route path="/todos/:todoId" exact children={<TodoPage />} />
                ) : null}
                <Route children={<NotFoundPage />} />
              </Switch>
            </DefaultLayout>
            :
            <Switch>
              <Route path="/" exact children={<LoginPage />} />
              {/* 受邀者尚未有帳號，這條路由必須在未登入狀態下可用 */}
              <Route path="/invite/:token" exact children={<InvitePage />} />
              {/* 從信件點進來的深層連結要能接住。HashRouter 的 location 在登入
                  前後不變，所以登入成功後已登入分支會自動比對到同一條路徑，
                  不需要另存「登入後要去哪裡」 */}
              <Route path="/todos/:todoId" exact children={<LoginPage />} />
              <Route children={<NotFoundPage />} />
            </Switch>
          }
        </Router>
      }
    </div>
  );
}

export default App;
